import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import NoteList, { TNoteListRef } from '@/components/NoteList'
import GroupedNoteList, { TGroupedNoteListRef } from '@/components/GroupedNoteList'
import Tabs from '@/components/Tabs'
import { Input } from '@/components/ui/input'
import { isTouchDevice } from '@/lib/utils'
import { TFeedSubRequest } from '@/types'
import KindFilter from './KindFilter'
import ProfileCombobox from './ProfileCombobox'
import { RefreshButton } from './RefreshButton'
import { groupedAtom } from '@/atoms/feed'
import { defaultShowKindsAtom } from '@/atoms/preferences'
import { useAtomValue, useSetAtom } from 'jotai'

function loadSelectedAuthors(storageKey: string): string[] {
  try {
    const raw = sessionStorage.getItem(storageKey)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

export default function NormalFeed({
  subRequests,
  showRelayCloseReason = false
}: {
  subRequests: null | TFeedSubRequest[]
  showRelayCloseReason?: boolean
}) {
  const { t } = useTranslation()
  const grouped = useAtomValue(groupedAtom)
  const setGrouped = useSetAtom(groupedAtom)

  const defaultShowKinds = useAtomValue(defaultShowKindsAtom)
  const [temporaryShowKinds, setTemporaryShowKinds] = useState<number[]>(() => defaultShowKinds)

  const [userFilter, setUserFilter] = useState('')
  const supportTouch = useMemo(() => isTouchDevice(), [])
  const noteListRef = useRef<TNoteListRef | TGroupedNoteListRef>(null)

  const tabs = useMemo(
    () => [
      { value: 'feed', label: 'Feed' },
      { value: 'grouped', label: 'Grouped' }
    ],
    []
  )

  const relayUrl = subRequests?.[0]?.source === 'relays' ? subRequests[0]?.urls?.[0] : undefined
  const storageKey = relayUrl ? `authors:${relayUrl}` : undefined

  const [selectedAuthors, setSelectedAuthors] = useState<string[]>(() =>
    storageKey ? loadSelectedAuthors(storageKey) : []
  )

  const filteredSubRequests = useMemo(() => {
    if (!subRequests || selectedAuthors.length === 0) return subRequests
    return subRequests.map((req) => ({
      ...req,
      filter: { ...req.filter, authors: selectedAuthors }
    }))
  }, [subRequests, selectedAuthors])

  return (
    <>
      <Tabs
        value={grouped ? 'grouped' : 'feed'}
        tabs={tabs}
        onTabChange={(tabValue: string) => {
          if (tabValue === 'grouped') {
            setGrouped(true)
          } else {
            setGrouped(false)
          }
          noteListRef.current?.scrollToTop('smooth')
        }}
        options={
          <>
            {storageKey && (
              <ProfileCombobox storageKey={storageKey} onChange={setSelectedAuthors} />
            )}
            {grouped && (
              <>
                <span className="hidden lg:block pl-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-search size-4 shrink-0 opacity-50"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.3-4.3"></path>
                  </svg>
                </span>

                <div className="relative">
                  <Input
                    type="text"
                    placeholder={t('Filter by author...')}
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    showClearButton={true}
                    onClear={() => setUserFilter('')}
                    className="h-9 shadow-none max-w-36 border-none bg-transparent focus:outline-none focus-visible:outline-none focus-visible:ring-0 placeholder:text-muted-foreground"
                  />
                </div>
              </>
            )}
            {!supportTouch && <RefreshButton onClick={() => noteListRef.current?.refresh()} />}
            <KindFilter
              showKinds={temporaryShowKinds}
              onShowKindsChange={(newShowKinds: number[]) => {
                setTemporaryShowKinds(newShowKinds)
                noteListRef.current?.scrollToTop()
              }}
            />
          </>
        }
      />
      {grouped ? (
        <GroupedNoteList
          ref={noteListRef as React.Ref<TGroupedNoteListRef>}
          showKinds={temporaryShowKinds}
          subRequests={subRequests}
          showRelayCloseReason={showRelayCloseReason}
          userFilter={userFilter}
        />
      ) : (
        <NoteList
          ref={noteListRef as React.Ref<TNoteListRef>}
          showKinds={temporaryShowKinds}
          subRequests={filteredSubRequests}
          showRelayCloseReason={showRelayCloseReason}
        />
      )}
    </>
  )
}
